import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Badge,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Chip,
  Divider,
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Menu as MenuIcon,
  Home as HomeIcon,
  Chair as ChairIcon,
  Bed as BedIcon,
  Kitchen as KitchenIcon,
  Bathtub as BathtubIcon,
} from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { logout } from '../../store/slices/authSlice';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const { items } = useSelector((state: RootState) => state.cart);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryMenuAnchor, setCategoryMenuAnchor] = useState<null | HTMLElement>(null);

  const categories = [
    { name: 'Living Room', icon: <ChairIcon />, path: '/products?category=living-room' },
    { name: 'Bedroom', icon: <BedIcon />, path: '/products?category=bedroom' },
    { name: 'Kitchen', icon: <KitchenIcon />, path: '/products?category=kitchen' },
    { name: 'Bathroom', icon: <BathtubIcon />, path: '/products?category=bathroom' },
  ];

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleCategoryMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCategoryMenuAnchor(event.currentTarget);
  };

  const handleCategoryMenuClose = () => {
    setCategoryMenuAnchor(null);
  };

  const cartItemCount = items.reduce((total, item) => total + item.quantity, 0);

  return (
    <>
      <AppBar position="sticky" elevation={0}>
        <Container maxWidth="xl">
          {/* Main Header */}
          <Toolbar sx={{ py: 1, minHeight: '64px' }}>
            {/* Logo */}
            <Typography
              variant="h5"
              component={Link}
              to="/"
              sx={{
                textDecoration: 'none',
                color: 'primary.main',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                mr: 4,
                fontSize: '1.5rem',
              }}
            >
              🏠 Dewfoam
            </Typography>

            {/* Categories Menu */}
            <Button
              startIcon={<MenuIcon />}
              onClick={handleCategoryMenuOpen}
              sx={{
                mr: 3,
                color: 'text.primary',
                fontWeight: 500,
                '&:hover': { backgroundColor: 'grey.50' },
              }}
            >
              Categories
            </Button>

            {/* Search Bar */}
            <Box component="form" onSubmit={handleSearch} sx={{ flexGrow: 1, maxWidth: 400, mr: 3 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search for furniture and interior items"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'grey.400' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'grey.50',
                    border: 'none',
                    '& fieldset': {
                      border: '1px solid',
                      borderColor: 'grey.200',
                    },
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: 1,
                    },
                  },
                }}
              />
            </Box>

            {/* User Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Cart */}
              <IconButton
                onClick={() => navigate('/cart')}
                sx={{ 
                  color: 'text.primary',
                  '&:hover': { backgroundColor: 'grey.100' },
                }}
              >
                <Badge 
                  badgeContent={cartItemCount} 
                  color="secondary"
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: '#FF7F2F',
                      color: 'white',
                    },
                  }}
                >
                  <ShoppingCartIcon />
                </Badge>
              </IconButton>

              {/* Authentication */}
              {isAuthenticated ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton sx={{ color: 'text.primary' }}>
                    <PersonIcon />
                  </IconButton>
                  <Typography variant="body2" sx={{ color: 'text.primary', mr: 1 }}>
                    Hello, {user?.firstName}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleLogout}
                    sx={{
                      borderColor: 'grey.300',
                      color: 'text.primary',
                      '&:hover': {
                        backgroundColor: 'grey.100',
                        borderColor: 'grey.400',
                      },
                    }}
                  >
                    Logout
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    component={Link}
                    to="/login"
                    sx={{ 
                      color: 'text.primary',
                      '&:hover': { backgroundColor: 'grey.100' },
                    }}
                  >
                    Login
                  </Button>
                  <Button
                    variant="contained"
                    component={Link}
                    to="/register"
                    sx={{
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    }}
                  >
                    Sign Up
                  </Button>
                </Box>
              )}
            </Box>
          </Toolbar>

          {/* Category Navigation */}
          <Box
            sx={{
              borderTop: '1px solid',
              borderColor: 'grey.200',
              py: 1,
            }}
          >
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', px: 2 }}>
              <Button
                component={Link}
                to="/"
                startIcon={<HomeIcon />}
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  fontWeight: 400,
                  '&:hover': { color: 'primary.main', backgroundColor: 'grey.50' },
                }}
              >
                Home
              </Button>
              {categories.map((category) => (
                <Button
                  key={category.name}
                  component={Link}
                  to={category.path}
                  startIcon={category.icon}
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    fontWeight: 400,
                    '&:hover': { color: 'primary.main', backgroundColor: 'grey.50' },
                  }}
                >
                  {category.name}
                </Button>
              ))}
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Chip
                label="New"
                size="small"
                sx={{
                  backgroundColor: 'secondary.main',
                  color: 'white',
                  fontWeight: 500,
                  '&:hover': { backgroundColor: 'secondary.dark' },
                }}
              />
              <Chip
                label="Best"
                size="small"
                sx={{
                  backgroundColor: 'grey.100',
                  color: 'text.primary',
                  '&:hover': { backgroundColor: 'grey.200' },
                }}
              />
            </Box>
          </Box>
        </Container>
      </AppBar>

      {/* Category Menu */}
      <Menu
        anchorEl={categoryMenuAnchor}
        open={Boolean(categoryMenuAnchor)}
        onClose={handleCategoryMenuClose}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 2,
            minWidth: 200,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        {categories.map((category) => (
          <MenuItem
            key={category.name}
            onClick={() => {
              navigate(category.path);
              handleCategoryMenuClose();
            }}
            sx={{ py: 1.5, gap: 1.5 }}
          >
            {category.icon}
            <Typography>{category.name}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default Header;